import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { getColumns, Attributes, operationURLS } from "./model";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import { application } from "globals/endpoints";
import CTLNotification from "application/components/CTLNotification";
import CTLBatteryStatus from "application/components/CTLBatteryStatus";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

const DEFAULT_PAGE_SIZE = 10;
const EXPANDED_PAGE_SIZE = 24;

const SORT_OPTIONS = [
  { value: "updatedOn", label: "Latest Update", sortDataType: "date" },
  { value: "extensionNumber", label: "Number", sortDataType: "string" },
  { value: "department", label: "Department", sortDataType: "string" },
];

const emptyFallback = (value, fallback = "--") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return value;
};

const formatDateTime = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  let parsedDate;

  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    const rawValue = Number(value);
    const timestamp = String(value).length === 10 ? rawValue * 1000 : rawValue;
    parsedDate = new Date(timestamp);
  } else {
    const normalizedValue =
      typeof value === "string" ? value.replace(" ", "T") : value;
    parsedDate = new Date(normalizedValue);
  }

  if (Number.isNaN(parsedDate.getTime())) {
    return "--";
  }

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

function DeviceAlert({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  onContactAction,
  isRightPanelExpanded = false,
  isCompactScreen = false,
}) {
  const theme = useTheme();
  const isSmallViewport = useMediaQuery(theme.breakpoints.down("md"));
  const [controller] = useMaterialUIController();
  const { activePage, darkMode } = controller;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationNames, setLocationNames] = useState({});
  const geocodeCacheRef = useRef({});
  const geocodeAttemptedRef = useRef({});
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("updatedOn");
  const [sortDirection, setSortDirection] = useState("desc");
  const [debouncedSearch, setDebouncedSearch] = useState(searchText || "");
  const fetchListRef = useRef(null);
  const currentPageRef = useRef(0);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "Device Alert",
    status: "",
  });
  const departmentList = AuthenticationService.getDepartmentList() || [];

  const hasLoadedRef = useRef(false);
  const latestFetchRequestRef = useRef(0);

  const selectedSortOption =
    SORT_OPTIONS.find((option) => option.value === sortBy) || SORT_OPTIONS[0];
  const resolvedSortDataType = selectedSortOption?.sortDataType || "";
  const isSmallView = isCompactScreen || isSmallViewport;
  const pageSize = useMemo(
    () =>
      isRightPanelExpanded && !isSmallView
        ? EXPANDED_PAGE_SIZE
        : DEFAULT_PAGE_SIZE,
    [isRightPanelExpanded, isSmallView],
  );
  const rowGridColumns = useMemo(() => {
    if (isSmallView) {
      return "1fr";
    }

    return isRightPanelExpanded
      ? "repeat(3, minmax(0, 1fr))"
      : "repeat(1, minmax(0, 1fr))";
  }, [isRightPanelExpanded, isSmallView]);

  // const panelGradient = darkMode
  //   ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
  //   : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)";

  const onOpenFilter = useCallback(() => {
    setOpenFilter(true);
  }, []);

  const onCloseFilter = useCallback(() => {
    setOpenFilter(false);
  }, []);

  const onApplyFilter = useCallback((filters) => {
    setAdvancedFilters(Array.isArray(filters) ? filters : []);
    setCurrentPage(0);
    setOpenFilter(false);
  }, []);

  useEffect(() => {
    if (typeof onLoad === "function") {
      onLoad(Attributes);
    }
  }, [onLoad, pageId]);

  useEffect(() => {
    if (typeof registerCallBacks === "function") {
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
      });
    }
  }, [activePage, onOpenFilter, registerCallBacks]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearch((searchText || "").trim());
      setCurrentPage(0);
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(0);
  }, [sortBy, sortDirection, advancedFilters]);

  useEffect(() => {
    hasLoadedRef.current = false;
    setCurrentPage(0);
  }, [pageSize]);

  const pendingGeoKeys = useMemo(() => {
    const keys = [];
    for (const item of rows) {
      if (item?.haddredd || item?.oaddress) continue;
      if (!item?.geoX || !item?.geoY) continue;
      const key = `${item.geoX},${item.geoY}`;
      if (!geocodeCacheRef.current[key] && !geocodeAttemptedRef.current[key]) {
        keys.push(key);
      }
    }
    return keys.sort().join("|");
  }, [rows]);

  useEffect(() => {
    if (!pendingGeoKeys) return;

    const keysToResolve = pendingGeoKeys.split("|");
    let cancelled = false;

    const resolve = async () => {
      const updates = {};
      const geocoder =
        window.google?.maps?.Geocoder
          ? new window.google.maps.Geocoder()
          : null;

      for (const key of keysToResolve) {
        if (cancelled) break;
        if (geocodeCacheRef.current[key]) {
          updates[key] = geocodeCacheRef.current[key];
          continue;
        }

        geocodeAttemptedRef.current[key] = true;

        try {
          const [latStr, lngStr] = key.split(",");
          const lat = parseFloat(latStr);
          const lng = parseFloat(lngStr);
          if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

          if (geocoder) {
            const result = await new Promise((res) =>
              geocoder.geocode({ location: { lat, lng } }, (results, status) =>
                res(
                  status === "OK" && results?.length
                    ? results[0].formatted_address
                    : null
                )
              )
            );
            if (result) {
              geocodeCacheRef.current[key] = result;
              updates[key] = result;
              continue;
            }
          }

          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=0`,
            { headers: { "Accept-Language": "en" } }
          );
          if (response.ok) {
            const data = await response.json();
            if (data?.display_name) {
              geocodeCacheRef.current[key] = data.display_name;
              updates[key] = data.display_name;
            }
          }
        } catch {
          // keep lat/long as fallback
        }
      }
      if (!cancelled && Object.keys(updates).length > 0) {
        setLocationNames((prev) => ({ ...prev, ...updates }));
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [pendingGeoKeys]);

  const fetchList = useCallback(
    async ({ page = 0, silent = false } = {}) => {
      const requestId = latestFetchRequestRef.current + 1;
      latestFetchRequestRef.current = requestId;
      const shouldShowLoader = !silent && !hasLoadedRef.current;

      if (shouldShowLoader) {
        setLoading(true);
      }

      try {
        const payload = {
          searchRequest: {
          currentPage: page,
          pageSize,
          sortDirection,
          sortBy,
          search: debouncedSearch,
          sortDataType: resolvedSortDataType,
          advancedFilters,
          },
           departmentList: departmentList?.map((dept) => dept.department),
        };

        const response = await application.post(operationURLS.list, payload);

        if (requestId !== latestFetchRequestRef.current) {
          return;
        }

        const responseData = response?.data?.data || {};
        const pageRows = Array.isArray(responseData.currentPageData)
          ? responseData.currentPageData
          : [];

        setRows(pageRows);
        setTotalPages(
          typeof responseData.totalPages === "number"
            ? responseData.totalPages
            : 0,
        );
        setTotalRecords(
          typeof responseData.totalRecords === "number"
            ? responseData.totalRecords
            : pageRows.length,
        );
        hasLoadedRef.current = true;
      } catch (error) {
        if (requestId !== latestFetchRequestRef.current) {
          return;
        }

        setNotify({
          isOpen: true,
          message:
            error?.response?.data?.message || "Device alert fetch failed",
          type: "error",
          pagename: "Device Alert",
          status:
            error?.response?.data?.messageDetail ||
            error?.message ||
            "Unable to fetch device alerts.",
        });
      } finally {
        if (shouldShowLoader && requestId === latestFetchRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [
      advancedFilters,
      debouncedSearch,
      pageSize,
      resolvedSortDataType,
      sortBy,
      sortDirection,
    ],
  );

  // Keep refs in sync so the interval can call the latest fetchList without
  // causing a state update (which would trigger a 50ms+ re-render violation).
  fetchListRef.current = fetchList;
  currentPageRef.current = currentPage;

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchListRef.current({ page: currentPageRef.current, silent: true });
    }, 5000);

    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    fetchList({
      page: currentPage,
      silent: false,
    });
  }, [currentPage, fetchList]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages - 1) {
      setCurrentPage(totalPages - 1);
    }

    if (totalPages === 0 && currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [currentPage, totalPages]);

  const canGoPrevious = currentPage > 0;
  const canGoNext = totalPages > 0 && currentPage < totalPages - 1;

  return (
    <MDBox sx={{ height: "100%", minHeight: 0, width: "100%", display: "flex" }}>
      <Card
        sx={{
          height: "100%",
          minHeight: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
          boxShadow: (theme) =>
            darkMode
              ? `0 4px 24px ${alpha(theme.palette.common.black, 0.4)}`
              : `0 2px 16px ${alpha(theme.palette.primary.main, 0.07)}`,
          p: { xs: 0.75, sm: 0.9 },
          gap: { xs: 0.55, sm: 0.7 },
          background: (theme) =>
            darkMode
              ? `linear-gradient(160deg, ${alpha(theme.palette.background.default, 0.98)} 0%, ${alpha(theme.palette.info.dark, 0.08)} 100%)`
              : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.info.light, 0.06)} 100%)`,
        }}
      >
        {/* Single toolbar row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            flexWrap: "nowrap",
          }}
        >
          <Chip
            label={`Total Records: ${totalRecords}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ height: 18, fontSize: "0.58rem", flexShrink: 0, "& .MuiChip-label": { px: 0.8 } }}
          />

          <MDTypography
            variant="caption"
            sx={{ fontSize: "0.63rem", fontWeight: 600, opacity: 0.7, flexShrink: 0 }}
          >
            Sort By
          </MDTypography>

          <Select
            size="small"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            sx={{
              flex: 1,
              maxWidth: 100,
              height: 26,
              fontSize: 11,
              borderRadius: 1.5,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                height: 26,
                py: 0,
                pr: 2.5,
                pl: 0.8,
              },
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: 12 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          <Tooltip title={sortDirection === "desc" ? "Descending" : "Ascending"}>
            <IconButton
              size="small"
              onClick={() =>
                setSortDirection((previous) => previous === "desc" ? "asc" : "desc")
              }
              sx={{
                width: 26,
                height: 26,
                flexShrink: 0,
                border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                borderRadius: 1.5,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              {sortDirection === "desc" ? (
                <ArrowDownwardOutlinedIcon sx={{ fontSize: 13 }} color="info" />
              ) : (
                <ArrowUpwardOutlinedIcon sx={{ fontSize: 13 }} color="info" />
              )}
            </IconButton>
          </Tooltip>

          <MDTypography variant="caption" color="text" sx={{ opacity: 0.5, fontSize: "0.58rem", flexShrink: 0 }}>
           Auto refresh: 5s
          </MDTypography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            maxHeight: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
            pr: 0.2,
            display: "grid",
            gap: { xs: 0.6, sm: 0.65 },
            gridTemplateColumns: rowGridColumns,
            alignContent: "start",
          }}
        >
          {loading && rows.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 130,
                gap: 1,
                gridColumn: "1 / -1",
              }}
            >
              <CircularProgress size={22} thickness={4} color="info" />
              <MDTypography variant="caption" color="text" sx={{ opacity: 0.5, fontSize: "0.6rem" }}>
                Loading alerts…
              </MDTypography>
            </Box>
          )}

          {!loading && rows.length === 0 && (
            <Card
              sx={{
                p: 1.5,
                border: (theme) => `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
                boxShadow: "none",
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1.5,
                background: "transparent",
              }}
            >
              <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                No device alerts found.
              </MDTypography>
            </Card>
          )}

          {rows.map((item) => {
            const identifier = item?.uuid || item?.id || item?.extensionNumber;
            const displayName = emptyFallback(
              item?.designation || item?.name || item?.username,
              "Unknown",
            );
            const extension = emptyFallback(item?.extensionNumber);
            const contactPayload = {
              id: identifier,
              offEXtn: String(item?.extensionNumber || "").trim(),
              extension: String(item?.extensionNumber || "").trim(),
              designation: item?.designation || item?.name || item?.username,
              name: item?.name || item?.designation || item?.username,
            };
            const geoKey = item?.geoX && item?.geoY ? `${item.geoX},${item.geoY}` : "";
            const location =
              item?.haddredd ||
              item?.oaddress ||
              locationNames[geoKey] ||
              (geoKey ? `${item.geoX}, ${item.geoY}` : "");
            const emergencyActive =
              (item?.emergencyStatus || "").toString().toLowerCase() ===
              "emergency";
            const connected = Boolean(item?.connected);
            const updatedAt = formatDateTime(item?.updatedOn);
            const eventTime = updatedAt;

            return (
              <Card
                key={identifier}
                sx={{
                  p: { xs: 0.75, sm: 0.8 },
                  minHeight: 74,
                  borderRadius: 1.5,
                  border: (theme) =>
                    `1px solid ${alpha(
                      emergencyActive ? theme.palette.error.main : theme.palette.info.main,
                      0.35,
                    )}`,
                  transition: "box-shadow 0.22s, transform 0.18s",
                  "&:hover": {
                    boxShadow: (theme) =>
                      emergencyActive
                        ? `0 0 16px ${alpha(theme.palette.error.main, 0.38)}`
                        : `0 0 16px ${alpha(theme.palette.info.main, 0.38)}`,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {/* Row 1: Name + Department + Time + Battery */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 0.55,
                    mb: 0.35,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        border: (theme) =>
                          `1.5px solid ${alpha(
                            emergencyActive ? theme.palette.error.main : theme.palette.info.main,
                            0.55,
                          )}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        background: (theme) =>
                          alpha(
                            emergencyActive ? theme.palette.error.main : theme.palette.info.main,
                            0.1,
                          ),
                      }}
                    >
                      <PersonOutlineIcon
                        sx={{
                          fontSize: 13,
                          color: emergencyActive ? "error.main" : "info.main",
                        }}
                      />
                    </Box>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{
                        lineHeight: 1.2,
                        fontSize: "0.61rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {displayName}
                    </MDTypography>
                    {emergencyActive && (
                      <Chip
                        label="SOS"
                        size="small"
                        color="error"
                        sx={{
                          height: 14,
                          fontSize: "0.5rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          "& .MuiChip-label": { px: 0.6 },
                        }}
                      />
                    )}
                  </Box>

                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{
                      fontSize: "0.58rem",
                      opacity: 0.72,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {item?.department || "-"}
                  </MDTypography>

                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{
                      fontSize: "0.58rem",
                      lineHeight: 1.1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      opacity: 0.65,
                      flexShrink: 0,
                    }}
                  >
                    {eventTime}
                  </MDTypography>

                  <Box
                    sx={{
                      minWidth: 38,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      flexShrink: 0,
                      "& .MuiTypography-root": {
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.2,
                        fontSize: "0.52rem",
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                      },
                      "& .MuiIconButton-root": { p: 0, mr: 0.1 },
                      "& .MuiSvgIcon-root": { fontSize: 16 },
                    }}
                  >
                    <CTLBatteryStatus value={item?.batteryPercentage} />
                  </Box>
                </Box>

                {/* Divider */}
                <Box
                  sx={{
                    height: "1px",
                    mx: -0.8,
                    mb: 0.4,
                    background: (theme) =>
                      `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.6)}, transparent)`,
                  }}
                />

                {/* Row 2: Location + Actions */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.4,
                      minWidth: 0,
                    }}
                  >
                    <LocationOnOutlinedIcon
                      sx={{ fontSize: 13, color: "warning.main", mt: 0.05, flexShrink: 0 }}
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{
                        fontSize: "0.59rem",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.35,
                        opacity: 0.8,
                      }}
                    >
                      {emptyFallback(location)}
                    </MDTypography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.4,
                      flexWrap: "nowrap",
                    }}
                  >
                    {[
                      { title: "Audio Call", action: "call", icon: <CallOutlinedIcon sx={{ fontSize: 13 }} />, color: "success" },
                      { title: "Video Call", action: "video-call", icon: <VideocamOutlinedIcon sx={{ fontSize: 13 }} />, color: "info" },
                      { title: "Ambient Mode", action: "ambient-mode", icon: <HearingOutlinedIcon sx={{ fontSize: 13 }} />, color: "error" },
                      { title: "Message", action: "message", icon: <ChatOutlinedIcon sx={{ fontSize: 13 }} />, color: "warning" },
                    ].map(({ title, action, icon, color }) => (
                      <Tooltip title={title} key={action}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onContactAction?.(action, contactPayload)}
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: "50%",
                              border: (theme) =>
                                `1px solid ${alpha(theme.palette[color].main, 0.4)}`,
                              backgroundColor: (theme) =>
                                alpha(theme.palette[color].main, 0.1),
                              color: `${color}.main`,
                              transition: "all 0.18s",
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  alpha(theme.palette[color].main, 0.22),
                                transform: "scale(1.12)",
                                boxShadow: (theme) =>
                                  `0 2px 8px ${alpha(theme.palette[color].main, 0.35)}`,
                              },
                            }}
                          >
                            {icon}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            pt: 0.2,
            borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.6, fontSize: "0.6rem" }}>
            Page {totalPages === 0 ? 0 : currentPage + 1} / {totalPages}
          </MDTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <IconButton
              size="small"
              color="text"
              disabled={!canGoPrevious}
              onClick={() =>
                setCurrentPage((previous) => Math.max(previous - 1, 0))
              }
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, canGoPrevious ? 0.4 : 0.15)}`,
                transition: "all 0.18s",
                "&:not(:disabled):hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              <NavigateBeforeOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>

            <IconButton
              size="small"
              color="text"
              disabled={!canGoNext}
              onClick={() =>
                setCurrentPage((previous) =>
                  totalPages > 0
                    ? Math.min(previous + 1, totalPages - 1)
                    : previous,
                )
              }
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, canGoNext ? 0.4 : 0.15)}`,
                transition: "all 0.18s",
                "&:not(:disabled):hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              <NavigateNextOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <CTLAdvancedFilter
        entityName="Devices Alert"
        columns={getColumns()}
        applyFilter={openFilter}
        onApplyFilter={onApplyFilter}
        onCloseFilter={onCloseFilter}
        advancedFilters={advancedFilters}
      />

      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}

export default DeviceAlert;
